import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBmiMeasurements, useSaveBmiMeasurement } from "@/queries/useBmiQuery";
import { ADULT_AGE, BMI_CATEGORIES, bmiCategory, calculateBmi, formatBmi, gaugePosition } from "./bmi";
import "./BmiPlugin.css";

// BMI calculator adapted from imc/bmi-calculator (MIT) for the Timeline panel:
// the patient comes from the consultation (no name field), the result updates
// while typing, and the consultation's measurement is saved on the server
// automatically (no save button, no history list).

// Display preferences of the doctor (no patient data), kept in the browser like the plugin list.
export const BMI_SETTINGS_STORAGE_KEY = "doctolik-plugin-bmi-settings";
export const DEFAULT_BMI_SETTINGS = { showGauge: true };
const HEIGHT_RANGE = { min: 100, max: 220 };
const LIMITS = { height: [50, 250], weight: [2, 400], age: [0, 130] };
const DEFAULT_VALUES = { sex: "male", age: "", heightCm: 170, weightKg: 70 };
const SAVE_DELAY_MS = 800;

export function readBmiSettings() {
	try {
		const saved = JSON.parse(localStorage.getItem(BMI_SETTINGS_STORAGE_KEY)) || {};
		return { showGauge: typeof saved.showGauge === "boolean" ? saved.showGauge : DEFAULT_BMI_SETTINGS.showGauge };
	} catch {
		return DEFAULT_BMI_SETTINGS;
	}
}

function useBmiSettings() {
	const [settings, setSettings] = useState(readBmiSettings);
	useEffect(() => {
		const refresh = (event) => {
			if (!event.key || event.key === BMI_SETTINGS_STORAGE_KEY) setSettings(readBmiSettings());
		};
		window.addEventListener("storage", refresh);
		return () => window.removeEventListener("storage", refresh);
	}, []);
	return settings;
}

const inRange = (value, [min, max]) => value !== "" && Number(value) >= min && Number(value) <= max;
export const isValidMeasurement = (values) => inRange(values.heightCm, LIMITS.height) && inRange(values.weightKg, LIMITS.weight) && (values.age === "" || inRange(values.age, LIMITS.age));
const digitsOnly = (value, maxLength) => {
	const digits = String(value).replace(/\D/g, "").slice(0, maxLength);
	return digits === "" ? "" : Number(digits);
};

function SexSelector({ value, onChange, disabled }) {
	return <div className="BmiSex" role="radiogroup" aria-label="Sexe">
		{[["male", "Homme"], ["female", "Femme"]].map(([id, label]) => <button
			key={id}
			type="button"
			role="radio"
			aria-checked={value === id}
			className={`BmiSexOption is-${id} ${value === id ? "is-selected" : ""}`}
			onClick={() => onChange(id)}
			disabled={disabled}
		>
			<span className="BmiSexIcon" aria-hidden="true"><svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg></span>
			{label}
		</button>)}
	</div>;
}

function Counter({ label, value, onChange, maxDigits, limits, disabled, optional = false }) {
	const [min, max] = limits;
	const step = (delta) => onChange(Math.min(max, Math.max(min, (value === "" ? (optional ? 30 : min) : Number(value)) + delta)));
	return <div className="BmiCounter">
		<span className="BmiCounterLabel">{label}</span>
		<div className="BmiCounterRow">
			<button type="button" className="BmiStep is-minus" onClick={() => step(-1)} disabled={disabled} aria-label={`Diminuer : ${label}`}>−</button>
			<input
				type="text"
				inputMode="numeric"
				aria-label={label}
				value={value}
				placeholder={optional ? "—" : undefined}
				onChange={(event) => {
					const next = digitsOnly(event.target.value, maxDigits);
					if (next === "" || next <= max) onChange(next);
				}}
				disabled={disabled}
			/>
			<button type="button" className="BmiStep is-plus" onClick={() => step(1)} disabled={disabled} aria-label={`Augmenter : ${label}`}>+</button>
		</div>
	</div>;
}

function BmiResult({ bmi, age, showGauge }) {
	if (bmi === null || bmi === undefined) {
		return <section className="BmiCard BmiResult is-empty"><h4>Résultat IMC</h4><p>Renseignez une taille et un poids valides pour obtenir l'IMC.</p></section>;
	}
	const info = BMI_CATEGORIES[bmiCategory(bmi)];
	return <section className="BmiCard BmiResult" aria-live="polite">
		<h4>Résultat IMC</h4>
		<p className="BmiValue">{formatBmi(bmi)} <small>kg/m²</small></p>
		{info && <p className="BmiBadge" style={{ color: info.color, background: info.background }}>{info.label}</p>}
		{showGauge && <div className="BmiGauge">
			<div className="BmiGaugeBar" aria-hidden="true">
				{Object.entries(BMI_CATEGORIES).map(([id, item]) => <span key={id} style={{ background: item.color }} />)}
				<i style={{ left: `${gaugePosition(bmi)}%` }} />
			</div>
			<div className="BmiGaugeLabels">{Object.entries(BMI_CATEGORIES).map(([id, item]) => <span key={id}>{item.label}<small>{item.range}</small></span>)}</div>
		</div>}
		{age !== "" && age !== null && age !== undefined && Number(age) < ADULT_AGE && <p className="BmiNote">Classification adulte (OMS) : chez l'enfant et l'adolescent, interprétez l'IMC avec les courbes de corpulence.</p>}
	</section>;
}

/** The calculator: the result follows the inputs; `onChange(values)` gets every valid user edit. */
export function BmiCalculator({ initialValues, settings, readOnly = false, status, onChange }) {
	const [values, setValues] = useState(() => ({ ...DEFAULT_VALUES, ...initialValues }));
	const valid = isValidMeasurement(values);
	const bmi = valid ? calculateBmi(values.heightCm, values.weightKg) : null;
	const update = (field, value) => {
		if (readOnly) return;
		const next = { ...values, [field]: value };
		setValues(next);
		if (isValidMeasurement(next)) onChange?.(next);
	};
	const heightForSlider = Math.min(HEIGHT_RANGE.max, Math.max(HEIGHT_RANGE.min, Number(values.heightCm) || HEIGHT_RANGE.min));
	const progress = ((heightForSlider - HEIGHT_RANGE.min) / (HEIGHT_RANGE.max - HEIGHT_RANGE.min)) * 100;

	return <div className="BmiLayout">
		<div className="BmiCard BmiForm">
			<SexSelector value={values.sex} onChange={(sex) => update("sex", sex)} disabled={readOnly} />
			<div className="BmiHeight">
				<label htmlFor="bmi-height">Taille (cm)</label>
				<input
					className="BmiHeightValue"
					type="text"
					inputMode="numeric"
					aria-label="Taille en centimètres"
					value={values.heightCm}
					onChange={(event) => { const next = digitsOnly(event.target.value, 3); if (next === "" || next <= LIMITS.height[1]) update("heightCm", next); }}
					disabled={readOnly}
				/>
				<input
					id="bmi-height"
					type="range"
					min={HEIGHT_RANGE.min}
					max={HEIGHT_RANGE.max}
					value={heightForSlider}
					style={{ "--bmi-progress": `${progress}%` }}
					onChange={(event) => update("heightCm", Number(event.target.value))}
					disabled={readOnly}
				/>
			</div>
			<div className="BmiCounters">
				<Counter label="Poids (kg)" value={values.weightKg} onChange={(value) => update("weightKg", value)} maxDigits={3} limits={LIMITS.weight} disabled={readOnly} />
				<Counter label="Âge" value={values.age} onChange={(value) => update("age", value)} maxDigits={3} limits={LIMITS.age} disabled={readOnly} optional />
			</div>
			{!valid && <p className="BmiError" role="alert">Taille entre {LIMITS.height[0]} et {LIMITS.height[1]} cm, poids entre {LIMITS.weight[0]} et {LIMITS.weight[1]} kg.</p>}
			{status}
		</div>
		<BmiResult bmi={bmi} age={values.age} showGauge={settings.showGauge} />
	</div>;
}

export function BmiPluginSettings({ onClose }) {
	const [settings, setSettings] = useState(readBmiSettings);
	// No save button: a preference is stored as soon as it changes.
	const update = (next) => {
		setSettings(next);
		localStorage.setItem(BMI_SETTINGS_STORAGE_KEY, JSON.stringify(next));
		// The storage event only reaches other tabs; notify this one too.
		window.dispatchEvent(new StorageEvent("storage", { key: BMI_SETTINGS_STORAGE_KEY }));
	};
	return <>
		<div className="PluginModalBody">
			<label><span>Afficher la jauge de corpulence</span><input type="checkbox" checked={settings.showGauge} onChange={(event) => update({ showGauge: event.target.checked })} /></label>
			<p className="BmiPluginHint">Aperçu : aucune donnée patient n'est utilisée ni enregistrée ici.</p>
			<div className="BmiPlugin"><BmiCalculator settings={settings} /></div>
		</div>
		<div className="PluginModalFooter">
			<button className="Btn" type="button" onClick={onClose}>Fermer</button>
		</div>
	</>;
}

const SAVE_LABELS = {
	idle: "",
	pending: "Modifications en attente…",
	saving: "Enregistrement…",
	saved: "IMC enregistré pour cette consultation",
	error: "Échec de l'enregistrement",
};

export default function BmiPlugin({ appointmentId, readOnly = false }) {
	const settings = useBmiSettings();
	// The latest measurement (this consultation's, else the patient's last one) seeds the form.
	const { data, isLoading, isError, refetch } = useBmiMeasurements(appointmentId, 1);
	const { mutateAsync } = useSaveBmiMeasurement(appointmentId);
	const [saveState, setSaveState] = useState("idle");
	const pending = useRef(null);
	const timer = useRef(null);

	const flush = useCallback(async () => {
		clearTimeout(timer.current);
		timer.current = null;
		const values = pending.current;
		if (!values) return;
		pending.current = null;
		setSaveState("saving");
		try {
			await mutateAsync(values);
			setSaveState(pending.current ? "pending" : "saved");
		} catch {
			pending.current ??= values;
			setSaveState("error");
		}
	}, [mutateAsync]);

	const handleChange = useCallback((values) => {
		if (readOnly) return;
		pending.current = values;
		setSaveState("pending");
		clearTimeout(timer.current);
		timer.current = setTimeout(flush, SAVE_DELAY_MS);
	}, [flush, readOnly]);

	// Do not lose the last edit when the doctor switches plugin or leaves the consultation.
	useEffect(() => () => {
		if (timer.current) flush();
	}, [flush]);

	// The form keeps its own state once shown: a save echoing back must not reset what is being typed.
	const [initialValues, setInitialValues] = useState(undefined);
	useEffect(() => {
		if (!data || initialValues !== undefined) return;
		const source = data.current ?? data.history?.[0] ?? null;
		setInitialValues(source ? { sex: source.sex ?? DEFAULT_VALUES.sex, age: source.age ?? "", heightCm: source.heightCm, weightKg: source.weightKg } : null);
	}, [data, initialValues]);

	if (!appointmentId) return <div className="BmiMessage">Ouvrez une consultation pour calculer l'IMC du patient.</div>;
	if (isError) return <div className="BmiMessage" role="alert">Impossible de charger l'IMC.<button type="button" onClick={() => refetch()}>Réessayer</button></div>;
	if (isLoading || initialValues === undefined) return <div className="BmiMessage" role="status">Chargement de l'IMC…</div>;

	return <div className="BmiPlugin">
		<BmiCalculator
			initialValues={initialValues ?? undefined}
			settings={settings}
			readOnly={readOnly}
			onChange={handleChange}
			status={saveState !== "idle" && <p className={`BmiSaveStatus is-${saveState}`} role="status">
				{SAVE_LABELS[saveState]}
				{saveState === "error" && <button type="button" onClick={flush}>Réessayer</button>}
			</p>}
		/>
	</div>;
}
